import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';
import { format12hClock } from '../../../utils/datetime';

// Time-only picker rendered as a styled native <select> of HH:MM slots.
// Sits alongside DateField / DateTimeField in the atoms set so callers stop
// reaching for native `<input type="time">` (whose look varies per browser).
// Default options are 15-min increments from 00:00 to 23:45; callers can
// override `options` for a different cadence.

// All 96 HH:MM slots in 15-minute increments, "00:00" through "23:45".
// Kept local to the atom so the dependency direction stays atom → nothing.
function generateTimeSlots() {
  const slots = [];
  for (let h = 0; h < 24; h += 1) {
    for (let m = 0; m < 60; m += 15) {
      const hh = String(h).padStart(2, '0');
      const mm = String(m).padStart(2, '0');
      slots.push(`${hh}:${mm}`);
    }
  }
  return slots;
}

// Inline SVG chevron baked into the select's background — interpolates the
// theme color so the chevron stays in sync if the muted text color shifts.
// `#` must be URL-encoded as `%23` inside an SVG data-URI.
const CHEVRON_STROKE = theme.colors.textMuted.replace('#', '%23');
const CHEVRON_SVG = `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'><path d='M2 4 L6 8 L10 4' stroke='${CHEVRON_STROKE}' stroke-width='1.5' fill='none' /></svg>")`;

const StyledSelect = styledComponent('select', {
  width: '100%',
  padding: theme.spacing.inputPadding,
  paddingRight: '32px',
  border: theme.borders.input,
  borderRadius: theme.radii.input,
  ...theme.typography.body,
  color: theme.colors.textBody,
  background: theme.colors.bgCard,
  backgroundImage: CHEVRON_SVG,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: `right ${theme.spacing.gapXl} center`,
  minHeight: theme.sizes.selectMinHeight,
  boxSizing: 'border-box',
  cursor: 'pointer',
  appearance: 'none',
  transition: theme.transitions.default,
  '&:hover:not(:disabled)': {
    borderColor: theme.colors.infoBlue,
  },
  '&:focus': {
    outline: 'none',
    borderColor: theme.colors.infoBlue,
    boxShadow: `0 0 0 2px ${theme.colors.infoBlue}33`,
  },
  '&:disabled': {
    background: theme.colors.bgMuted,
    color: theme.colors.textMuted,
    cursor: 'not-allowed',
  },
});

function TimeField({
  id,
  value,
  onChange,
  options,
  includeEmpty,
  emptyLabel,
  disabled,
  'aria-label': ariaLabel,
  'data-testid': dataTestId,
}) {
  const slots = useMemo(() => options || generateTimeSlots(), [options]);

  return (
    <StyledSelect
      id={id}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      aria-label={ariaLabel}
      data-testid={dataTestId}
    >
      {includeEmpty && <option value="">{emptyLabel}</option>}
      {slots.map((slot) => (
        <option key={slot} value={slot}>
          {format12hClock(slot)}
        </option>
      ))}
    </StyledSelect>
  );
}

TimeField.propTypes = {
  id: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.string),
  includeEmpty: PropTypes.bool,
  emptyLabel: PropTypes.string,
  disabled: PropTypes.bool,
  'aria-label': PropTypes.string,
  'data-testid': PropTypes.string,
};

TimeField.defaultProps = {
  id: undefined,
  value: '',
  options: null,
  includeEmpty: false,
  emptyLabel: 'Select time…',
  disabled: false,
  'aria-label': undefined,
  'data-testid': undefined,
};

export default TimeField;
